/**
 * Markdown → HTML renderer for chapter articles.
 *
 * Pipeline:
 *   raw markdown
 *     → shared safe GFM renderer        # same output as Worker SSR
 *     → return as HTML string
 *
 * `mermaid.run()` must be called separately by the caller after the
 * HTML is inserted into the DOM, because mermaid needs to find the
 * divs in the live document.
 *
 * Core article Markdown is bundled with the app so ordinary Markdown has no
 * runtime chunk boundary. Mermaid stays lazy because diagrams
 * are optional and its renderer is substantially larger.
 */
import { renderSafeGfmMarkdown } from '../../shared/markdown/safeGfmMarkdown';
import { getMermaidThemeConfig } from './mermaidTheme';
import { bakeMermaidSvgSelfContainedStyles, reconcileMermaidLabelContrast } from './mermaidStyleBaking';
import { wireMermaidFullscreenBlocks } from './mermaidFullscreen';

let mermaidReady: Promise<typeof import("mermaid").default> | null = null;
async function getMermaid() {
  if (!mermaidReady) {
    mermaidReady = (async () => {
      const mod = await import("mermaid");
      const mermaid = mod.default;
      (mermaid as unknown as { setParseErrorHandler?: (handler: (...args: unknown[]) => void) => void })
        .setParseErrorHandler?.(() => undefined);
      return mermaid;
    })();
  }
	const loading = mermaidReady;
	try {
		return await loading;
	} catch (error) {
		if (mermaidReady === loading) mermaidReady = null;
		throw error;
	}
}

/**
 * Render Markdown with the same safe GFM policy used by the Worker SSR.
 */
export async function renderMarkdownToSafeHtml(md: string): Promise<string> {
  try {
    return renderSafeGfmMarkdown(md);
  } catch {
    const escaped = md
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
    return `<pre style="white-space:pre-wrap;">${escaped}</pre>`;
  }
}

const SEQUENCE_RESERVED_ALIASES = new Set([
  "loop",
  "end",
  "par",
  "opt",
  "alt",
  "critical",
  "break",
  "rect",
  "note",
]);

/**
 * Mermaid sequenceDiagram actor aliases are parsed case-insensitively
 * against control keywords. `participant Loop as Worker Loop` renders
 * as a normal participant visually, but later `Loop->>DB` is tokenized
 * as the `loop` control keyword in Mermaid 11.15.0. Rename only the
 * internal alias and keep the display label unchanged.
 */
export function normalizeMermaidSource(source: string): string {
  const lines = source.replace(/\r\n?/g, "\n").split("\n");
  if (!/^\s*sequenceDiagram\b/.test(lines[0] ?? "")) return source;

  const aliases = new Map<string, string>();
  const participantPattern = /^(\s*participant\s+)([A-Za-z][\w-]*)(\s+as\s+.+)$/;
  const normalized = lines.map((line) => {
    const match = participantPattern.exec(line);
    if (!match) return line;
    const alias = match[2];
    if (!SEQUENCE_RESERVED_ALIASES.has(alias.toLowerCase())) return line;
    const replacement = `Seq${alias}`;
    aliases.set(alias, replacement);
    return `${match[1]}${replacement}${match[3]}`;
  });

  if (aliases.size === 0) return lines.join("\n");
  return normalized.map((line) => {
    if (participantPattern.test(line)) return line;
    let out = line;
    aliases.forEach((replacement, alias) => {
      out = out.replace(new RegExp(`\\b${escapeRegExp(alias)}\\b`, "g"), replacement);
    });
    return out;
  }).join("\n");
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * After the rendered HTML is in the DOM, find every <div class="mermaid">
 * and ask mermaid to draw it. Idempotent — re-runs the same source into
 * the same node will replace, not duplicate.
 */
const mermaidSources = new WeakMap<HTMLElement, string>();
let mermaidRenderQueue: Promise<void> = Promise.resolve();
let mermaidRenderId = 0;
let themeListenerInstalled = false;

function installThemeListener(): void {
	if (themeListenerInstalled || typeof window === 'undefined' || typeof document === 'undefined') return;
	themeListenerInstalled = true;
	window.addEventListener('deepmox:themechange', () => {
		void renderMermaidBlocks(document).catch(() => undefined);
	});
}

async function renderMermaidBlocksNow(container: HTMLElement | Document, resolvedTheme: string | undefined): Promise<void> {
	const blocks = container instanceof HTMLElement && container.classList.contains('mermaid')
		? [container]
		: Array.from(container.querySelectorAll<HTMLElement>('.mermaid'));
	if (blocks.length === 0) return;
	let mermaid: Awaited<ReturnType<typeof getMermaid>>;
	try {
		mermaid = await getMermaid();
	} catch {
		for (const block of blocks) renderMermaidUnavailable(block, mermaidSources.get(block) ?? block.textContent ?? '');
		return;
	}
	try {
	mermaid.initialize({
		startOnLoad: false,
		securityLevel: 'strict',
		suppressErrorRendering: true,
		fontFamily: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif',
		...getMermaidThemeConfig(resolvedTheme),
	} as Parameters<typeof mermaid.initialize>[0] & { suppressErrorRendering?: boolean });
	} catch {
		for (const block of blocks) renderMermaidUnavailable(block, mermaidSources.get(block) ?? block.textContent ?? '');
		return;
	}
	for (const block of blocks) {
		const source = mermaidSources.get(block) ?? block.textContent ?? '';
		mermaidSources.set(block, source);
		try {
			const { svg } = await mermaid.render(`mmd-${Date.now()}-${mermaidRenderId++}`, normalizeMermaidSource(source));
			block.innerHTML = svg;
			const svgElement = block.querySelector('svg');
			// Safari Reader Mode drops every stylesheet (page CSS and the
			// SVG-internal theme sheet), collapsing the diagram to black
			// defaults. Bake the final colors into attributes/inline styles so
			// the SVG is self-contained; normal rendering is unaffected because
			// presentation attributes lose to stylesheet rules.
			if (svgElement) {
				// Authored `style X fill:…` statements set no text color; pastel
				// fills under the dark theme's near-white labels render unreadable.
				// Reconcile the label ink per node BEFORE baking so the fix is
				// carried into stylesheet-stripping environments (Reader) too.
				reconcileMermaidLabelContrast(svgElement as SVGSVGElement);
				bakeMermaidSvgSelfContainedStyles(svgElement as SVGSVGElement);
			}
		} catch {
			renderMermaidUnavailable(block, source);
		}
	}
	wireMermaidFullscreenBlocks(container);
}

function renderMermaidUnavailable(block: HTMLElement, source: string): void {
	mermaidSources.set(block, source);
	const status = document.createElement('div');
	status.className = 'mermaid-unavailable';
	const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
	icon.setAttribute('viewBox', '0 0 24 24');
	icon.setAttribute('width', '16');
	icon.setAttribute('height', '16');
	icon.setAttribute('aria-hidden', 'true');
	const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
	path.setAttribute('d', 'M12 8v4m0 4h.01M10.3 3.9 2.6 17.2A2 2 0 0 0 4.3 20h15.4a2 2 0 0 0 1.7-2.8L13.7 3.9a2 2 0 0 0-3.4 0Z');
	path.setAttribute('fill', 'none');
	path.setAttribute('stroke', 'currentColor');
	path.setAttribute('stroke-width', '1.8');
	path.setAttribute('stroke-linecap', 'round');
	path.setAttribute('stroke-linejoin', 'round');
	icon.appendChild(path);
	const message = document.createElement('span');
	message.textContent = 'Diagram unavailable';
	const retry = document.createElement('button');
	retry.type = 'button';
	retry.className = 'btn btn-ghost btn-sm mermaid-retry';
	retry.textContent = 'Retry';
	retry.addEventListener('click', () => {
		block.textContent = source;
		void renderMermaidBlocks(block);
	});
	status.appendChild(icon);
	status.appendChild(message);
	status.appendChild(retry);
	const sourceElement = document.createElement('pre');
	sourceElement.className = 'mermaid-source';
	sourceElement.textContent = source;
	block.replaceChildren(status, sourceElement);
}

export function renderMermaidBlocks(container: HTMLElement | Document): Promise<void> {
	installThemeListener();
	const resolvedTheme = document.documentElement.dataset.theme;
	const task = mermaidRenderQueue.then(
		() => renderMermaidBlocksNow(container, resolvedTheme),
		() => renderMermaidBlocksNow(container, resolvedTheme),
	);
	mermaidRenderQueue = task.catch(() => undefined);
	return mermaidRenderQueue;
}
